import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ShoppingBag, Loader2, Check, Sparkles, Crown, Star, Zap, Flame, Skull } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BannerDisplay, RARITY_META } from "@/components/Banner";
import { HelpRequestButton } from "@/components/HelpRequestButton";

const RARITY_ICONS = {
    common: Sparkles,
    rare: Zap,
    epic: Flame,
    legendary: Crown,
    mythic: Star,
};

export default function Shop() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [equippedId, setEquippedId] = useState(null);

    const fetchShop = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/shop/banners");
            setItems(res.data.items || []);
            setEquippedId(res.data.equipped_id || null);
        } catch (e) { /* noop */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchShop(); }, [fetchShop, user]);

    const buy = async (banner) => {
        if (!user) {
            toast("Log in to buy banners");
            nav("/login?next=/shop");
            return;
        }
        setBusy(banner.id);
        try {
            const res = await api.post("/shop/banners/buy", { banner_id: banner.id, origin_url: window.location.origin });
            if (res.data.granted) {
                toast.success("Banner unlocked!");
                await fetchShop();
            } else if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Purchase failed");
        } finally {
            setBusy(null);
        }
    };

    const equip = async (banner) => {
        const isEquipped = equippedId === banner.id;
        try {
            const res = await api.post("/shop/banners/equip", { banner_id: isEquipped ? null : banner.id });
            setEquippedId(res.data.equipped_banner_id);
            toast.success(isEquipped ? "Banner unequipped" : "Banner equipped \u2014 see your profile!");
            await refresh();
        } catch (e) {
            toast.error("Failed");
        }
    };

    const featured = items.find((b) => b.featured);

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-10 sm:py-14">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="mb-8">
                        <p className="text-xs font-mono uppercase text-[#a855f7] tracking-widest">Banner Shop</p>
                        <h1 className="font-display text-4xl sm:text-5xl tracking-widest text-white inline-flex items-center gap-2">
                            <ShoppingBag className="w-7 h-7 text-[#facc15]" /> EQUIP YOUR FLEX
                        </h1>
                        <p className="mt-1 text-sm text-[#b7b7c6]">Custom AI-generated banners. Bigger price = cooler animation. New drops weekly.</p>
                    </div>

                    {featured && featured.has_image && (
                        <div className="mb-8" data-testid="shop-featured">
                            <p className="text-xs font-mono uppercase text-[#facc15] tracking-widest mb-2 inline-flex items-center gap-1">
                                <Star className="w-3 h-3" /> Featured this week
                            </p>
                            <BannerDisplay banner={featured} height={180} />
                            <p className="mt-2 text-sm text-white"><span className="font-display tracking-widest">{featured.name}</span> · <span style={{ color: RARITY_META[featured.rarity].color }}>{RARITY_META[featured.rarity].label}</span> · ${featured.price.toFixed(2)}</p>
                        </div>
                    )}

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {[1,2,3,4,5,6].map((i) => <Skeleton key={i} className="h-72 rounded-xl pp-bg-1" />)}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-banner-grid">
                            {items.map((b) => {
                                const RarityIcon = RARITY_ICONS[b.rarity] || Sparkles;
                                const rarityColor = RARITY_META[b.rarity]?.color || "#a855f7";
                                return (
                                    <motion.div key={b.id}
                                        initial={{ opacity: 0, y: 14 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4 }}
                                        className="pp-bg-1 rounded-2xl border border-[#242436] overflow-hidden"
                                        style={{ boxShadow: `0 0 0 1px ${rarityColor}33, 0 0 22px ${rarityColor}28` }}
                                        data-testid={`shop-banner-${b.slug}`}
                                    >
                                        <BannerDisplay banner={b} height={140} className="rounded-none border-0" />
                                        <div className="p-4">
                                            <div className="flex items-center justify-between">
                                                <p className="font-display text-xl tracking-widest text-white">{b.name}</p>
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase" style={{ color: rarityColor, borderColor: `${rarityColor}66`, background: `${rarityColor}14`, border: `1px solid ${rarityColor}55` }}>
                                                    <RarityIcon className="w-3 h-3" /> {RARITY_META[b.rarity].label}
                                                </span>
                                            </div>
                                            <p className="text-xs text-[#8b8ba0] mt-1 capitalize">{b.animation.replaceAll("_", " ")}</p>
                                            <div className="mt-4 flex items-center justify-between gap-2">
                                                <span className="font-display text-3xl tracking-widest" style={{ color: rarityColor }}>${b.price.toFixed(2)}</span>
                                                {b.owned ? (
                                                    <Button onClick={() => equip(b)} disabled={!b.has_image} className={equippedId === b.id ? "bg-[#22c55e] text-[#07130b] font-semibold" : "bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white"} data-testid={`shop-equip-button-${b.slug}`}>
                                                        {equippedId === b.id ? <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Equipped</span> : "Equip"}
                                                    </Button>
                                                ) : (
                                                    <Button onClick={() => buy(b)} disabled={busy === b.id || !b.has_image} className="text-[#07130b] font-semibold" style={{ background: `linear-gradient(90deg, ${rarityColor}, #6f3fec)` }} data-testid={`shop-buy-button-${b.slug}`}>
                                                        {busy === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy"}
                                                    </Button>
                                                )}
                                            </div>
                                            {!b.has_image && (
                                                <p className="mt-2 text-xs text-[#8b8ba0]">Image generating… check back soon.</p>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
            <Footer />
            <HelpRequestButton />
        </div>
    );
}
